import { useMutation } from '@apollo/client';
import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import { getAdminTenantsSummaryFilterQuery } from './query';

const NewTenantMutation = graphql(`
  mutation newTenant($tenant: TenantsInsertInput!) {
    insertTenantsOne(object: $tenant) {
      __typename
      id
      name
      createdAt
      isPendingApproval
      appointmentsAggregate {
        aggregate {
          count
        }
      }
      tenantUsersAggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

export const NewTenantAction = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const toast = useToast();

  const [insertTenant] = useMutation(NewTenantMutation, {
    onCompleted: (data) => {
      const newTenant = data?.insertTenantsOne;
      if (!newTenant) return;
      toast.success('New organization added successfully');

      setIsDialogOpen(false);
    },
    onError: () => {
      toast.error('Failed to add new organization');
    },
    refetchQueries: [getAdminTenantsSummaryFilterQuery],
  });

  const handleDialogOpen = (open: boolean) => {
    if (open) {
      setNewTenantName('');
      setIsDialogOpen(open);
      return;
    }

    if (
      newTenantName &&
      !window.confirm('Are you sure you want to discard changes?')
    ) {
      return;
    }

    setIsDialogOpen(false);
  };

  const handleAddTenant = () => {
    insertTenant({
      variables: {
        tenant: {
          name: newTenantName,
          isPendingApproval: false,
        },
      },
    });
    setIsDialogOpen(false);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogOpen} modal>
      <DialogTrigger asChild>
        <Button>
          <Icon name="Plus" size="xl" />
          New Organization
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>New Organization</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Enter new organization name"
          value={newTenantName}
          onChange={(e) => setNewTenantName(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddTenant}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
